import type { InfiniteData } from '@tanstack/query-core';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { RefCallback, useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { isConversationTypeActive } from '../../App/Sidebar/components/SidebarInboxButton';
import { InboxStatus, UserInboxStatus } from '../../SocketProvider';
import { QueryKey } from '@/Config/api/queryKey';
import { InboxTypeEnum, IndexPath } from '@/Config/app/router/index.path';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';
import { ConversationItem } from '@/Domain/components/Inbox/ConversationList/ConversationItem';
import { EmptyInbox } from '@/Domain/components/Inbox/EmptyInbox/EmptyInbox';
import { TeammateFilter } from '@/Domain/components/Inbox/TeammateFilter/TeammateFilter';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Tabs, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';

type Props = {
  isLoading: boolean;
  loading: boolean;
  hasNextPage: boolean;
  conversations: InfiniteData<Paginated<ConversationDto[]>, unknown> | undefined;
  sentryRef: RefCallback<HTMLElement>;
  isConversationPanelActive: boolean;
};

enum ConversationTypeEnum {
  All = 'all',
  Open = 'open',
  OpenAssigned = 'openAssigned',
  OpenUnassigned = 'openUnassigned',
  Resolved = 'resolved',
}

enum InboxTabEnum {
  OpenByAI = 'openByAI',
  OpenByMe = 'openByMe',
  OpenByOther = 'openByOther',
  ResolvedByHuman = 'resolvedByHuman',
  AssumedResolvedByAI = 'assumedResolvedByAI',
}

type InboxTabData = Readonly<{
  key: InboxTabEnum;
  label: string;
  redirectPath: string;
  inboxType: InboxTypeEnum;
}>;

function tabCount(
  inboxTab: InboxTabEnum,
  inboxStatusData: InboxStatus | undefined,
  userInboxStatus: UserInboxStatus | undefined,
): number {
  switch (inboxTab) {
    case InboxTabEnum.OpenByAI:
      return inboxStatusData
        ? inboxStatusData.open_conversations_counts.ai_assistant_conversations_count
        : 0;
    case InboxTabEnum.OpenByMe:
      return userInboxStatus ? userInboxStatus.my_conversations_count : 0;
    case InboxTabEnum.OpenByOther:
      return userInboxStatus ? userInboxStatus.other_conversations_count : 0;
    case InboxTabEnum.ResolvedByHuman:
      return inboxStatusData
        ? inboxStatusData.resolved_conversations_counts.human_conversations_count
        : 0;
    case InboxTabEnum.AssumedResolvedByAI:
      return inboxStatusData
        ? inboxStatusData.resolved_conversations_counts.ai_assistant_conversations_count
        : 0;
  }
}

type TabProps = {
  activeTab?: string;
  setActiveTab: (tab: string) => void;
  tabs: readonly InboxTabData[];
  inboxStatusData: InboxStatus | undefined;
  userInboxStatus: UserInboxStatus | undefined;
};

const InboxTabs = ({
  activeTab,
  setActiveTab,
  tabs,
  inboxStatusData,
  userInboxStatus,
}: TabProps) => {
  const navigate = useNavigate();

  return (
    <Tabs
      value={activeTab}
      onValueChange={(key) => {
        setActiveTab(key);
        const inboxTabData: InboxTabData | undefined = tabs.find((tabData) => tabData.key === key);
        if (inboxTabData) navigate(inboxTabData.redirectPath);
      }}
      className={classNames({
        hidden: tabs.length === 0,
      })}
    >
      <TabsList
        className="
            mb-3
            w-full
            flex-nowrap
            overflow-x-auto overflow-y-hidden
            whitespace-nowrap
            [-webkit-overflow-scrolling:touch]
            scrollbar-none
          "
      >
        {tabs.map((inboxTab) => (
          <TabsTrigger value={inboxTab.key} key={inboxTab.key} className="flex-1">
            {inboxTab.label}
            {tabCount(inboxTab.key, inboxStatusData, userInboxStatus) > 0 ? (
              <Badge
                variant={inboxTab.key === InboxTabEnum.OpenByMe ? 'error' : 'secondary'}
                size="sm"
              >
                {tabCount(inboxTab.key, inboxStatusData, userInboxStatus)}
              </Badge>
            ) : null}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};

export const ConversationList = ({
  conversations,
  loading,
  hasNextPage,
  sentryRef,
  isLoading,
  isConversationPanelActive,
}: Props) => {
  const { t } = useTranslation('sidebar_menu', { keyPrefix: 'menu' });
  const inboxTabToInboxTabData: Record<InboxTabEnum, InboxTabData> = useMemo(
    () => ({
      [InboxTabEnum.OpenByMe]: {
        key: InboxTabEnum.OpenByMe,
        label: t('conversations.openByMe'),
        redirectPath: IndexPath.inbox.openByMe,
        inboxType: InboxTypeEnum.OpenByMe,
      },
      [InboxTabEnum.OpenByOther]: {
        key: InboxTabEnum.OpenByOther,
        label: t('conversations.openByOther'),
        redirectPath: IndexPath.inbox.openByOther,
        inboxType: InboxTypeEnum.OpenByOther,
      },
      [InboxTabEnum.OpenByAI]: {
        key: InboxTabEnum.OpenByAI,
        label: t('conversations.openByAI'),
        redirectPath: IndexPath.inbox.openByAI,
        inboxType: InboxTypeEnum.OpenByAI,
      },
      [InboxTabEnum.ResolvedByHuman]: {
        key: InboxTabEnum.ResolvedByHuman,
        label: t('conversations.resolvedByHuman'),
        redirectPath: IndexPath.inbox.resolvedByHuman,
        inboxType: InboxTypeEnum.ResolvedByHuman,
      },
      [InboxTabEnum.AssumedResolvedByAI]: {
        key: InboxTabEnum.AssumedResolvedByAI,
        label: t('conversations.assumedResolvedByAI'),
        redirectPath: IndexPath.inbox.assumedResolvedByAI,
        inboxType: InboxTypeEnum.AssumedResolvedByAI,
      },
    }),
    [t],
  );

  const conversationToInboxTypes: Record<ConversationTypeEnum, readonly InboxTabData[]> = {
    [ConversationTypeEnum.Open]: [],
    [ConversationTypeEnum.OpenAssigned]: [],
    [ConversationTypeEnum.OpenUnassigned]: [],
    [ConversationTypeEnum.Resolved]: [
      inboxTabToInboxTabData[InboxTabEnum.AssumedResolvedByAI],
      inboxTabToInboxTabData[InboxTabEnum.ResolvedByHuman],
    ],
    [ConversationTypeEnum.All]: [],
  };

  const [hoverConversationId, setHoverConversationId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const inboxType = searchParams.get('type') as InboxTypeEnum | null;

  const isOpenAssigned = [
    InboxTypeEnum.OpenByAI,
    InboxTypeEnum.OpenByMe,
    InboxTypeEnum.OpenByOther,
  ].includes(inboxType as InboxTypeEnum);

  const { data: inboxStatusData } = useQuery<InboxStatus>({
    queryKey: [QueryKey.inboxStatus.general],
  });
  const { data: userInboxStatusData } = useQuery<UserInboxStatus>({
    queryKey: [QueryKey.inboxStatus.my],
  });

  const allConversationTypes = Object.values(ConversationTypeEnum) as ConversationTypeEnum[];
  const inboxTabs: InboxTabData[] = useMemo(
    () =>
      allConversationTypes.flatMap((conversationType: ConversationTypeEnum) =>
        isConversationTypeActive(conversationType, inboxType)
          ? conversationToInboxTypes[conversationType]
          : [],
      ),
    [inboxType],
  );

  const [activeTab, setActiveTab] = useState<string>();

  useEffect(() => {
    const tabFromInboxType = inboxTabs.find((tabData) => tabData.inboxType === inboxType);

    if (tabFromInboxType?.key === activeTab) return;

    if (!tabFromInboxType && !!activeTab) {
      if (inboxTabs.map((inboxTab) => inboxTab.key).includes(activeTab as InboxTabEnum)) return;
    }

    const firstInboxTab: InboxTabData | undefined = tabFromInboxType
      ? tabFromInboxType
      : inboxTabs
        ? inboxTabs[0]
        : undefined;
    if (!firstInboxTab) return;

    setActiveTab(firstInboxTab.key);
    navigate(firstInboxTab.redirectPath);
  }, [inboxTabs, activeTab, setActiveTab, searchParams]);

  // Get all conversations as a flat array
  const allConversations = conversations?.pages.map(({ items }) => items).flat() || [];
  const locationSearch = location.search;

  useHotkeys(
    'arrowdown',
    (event) => {
      if (allConversations.length === 0) return;
      event.preventDefault();
      const currentIndex = allConversations.findIndex((c) => location.pathname.includes(c.id));
      if (currentIndex >= 0 && currentIndex < allConversations.length - 1) {
        navigate(`${allConversations[currentIndex + 1].id + locationSearch}`);
      } else if (currentIndex === -1 && allConversations.length > 0) {
        navigate(`${allConversations[0].id + locationSearch}`);
      }
    },
    {
      enabled: !isConversationPanelActive,
      enableOnFormTags: false,
      enableOnContentEditable: false,
      preventDefault: true,
    },
    [allConversations, navigate, location.pathname, locationSearch, isConversationPanelActive],
  );

  useHotkeys(
    'arrowup',
    (event) => {
      if (allConversations.length === 0) return;
      event.preventDefault();
      const currentIndex = allConversations.findIndex((c) => location.pathname.includes(c.id));
      if (currentIndex > 0) {
        navigate(`${allConversations[currentIndex - 1].id + locationSearch}`);
      }
    },
    {
      enabled: !isConversationPanelActive,
      enableOnFormTags: false,
      enableOnContentEditable: false,
      preventDefault: true,
    },
    [allConversations, navigate, location.pathname, locationSearch, isConversationPanelActive],
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-1 justify-center items-center ">
          <Spinner className="h-7 w-7" />
        </div>
      );
    }

    if (conversations?.pages.map(({ items }) => items).flat().length === 0) {
      return <EmptyInbox />;
    }

    return (
      <ul className="list-none flex-1 overflow-y-auto hide-scrollbar">
        {allConversations.map((conversation, index, array) => {
          const isActive = location.pathname.includes(conversation.id);
          const isNextElementActive =
            index < array.length - 1 ? location.pathname.includes(array[index + 1].id) : false;

          const hoverElementIndex = array.findIndex((item) => item.id === hoverConversationId);

          return (
            <div className="group" key={conversation.id}>
              <ConversationItem
                conversation={conversation}
                key={conversation.id}
                isActive={isActive}
                onHoverChange={(hovering) => {
                  setHoverConversationId(hovering ? conversation.id : null);
                }}
              />
              <Separator
                className={classNames('group-hover:bg-transparent', {
                  'bg-transparent':
                    isActive || isNextElementActive || hoverElementIndex - 1 === index,
                })}
              />
            </div>
          );
        })}
        {(loading || hasNextPage) && (
          <li ref={sentryRef}>
            <div className="flex justify-center items-center py-6">
              <Spinner />
            </div>
          </li>
        )}
      </ul>
    );
  };

  return (
    <>
      <InboxTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={inboxTabs}
        inboxStatusData={inboxStatusData}
        userInboxStatus={userInboxStatusData}
      />
      {isOpenAssigned && <TeammateFilter />}
      {renderContent()}
    </>
  );
};
